<template>
    <div>
        <v-card class="price-levelings" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('price_levelings.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            class="mr-2"
                            dark
                            outlined
                            @click="exportToFile"
                        >
                            <v-icon left>fal fa-download</v-icon>
                            {{ $t('general.export') }}
                        </v-btn>

                        <v-btn
                            class="minw100"
                            color="white"
                            outlined
                            @click="openForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <price-levelings-search
                    :filter.sync="filter"
                />

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:foot="{ items }">
                        <tfoot>
                            <tr>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td>{{ $t('general.total').toUpperCase() }}:</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getOldSellingValue()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getOldTax()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getOldSellingValueAfterTax()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getSellingValue()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getSellingValueAfterTax()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getPriceDifference()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </template>
                    <template v-slot:item="{ item }">
                        <tr>
                            <td>{{ item.date | date }}</td>
                            <td>
                                <v-chip
                                    style="z-index: 0"
                                    small
                                    outlined
                                    :color="item.recorded_at ? 'success' : 'error'"
                                >
                                    {{ item.recorded_at ? $t('general.yes') : $t('general.no') }}
                                </v-chip>
                            </td>
                            <td>{{ item.number }}</td>
                            <td>{{ item.description }}</td>
                            <td class="error--text font-weight-bold">{{ item.getOldSellingValue().toFormat('0.00') }}</td>
                            <td>{{ item.getOldTax().toFormat('0.00') }}</td>
                            <td class="error--text font-weight-bold">{{ item.getOldSellingValueAfterTax().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValue().toFormat('0.00') }}</td>
                            <td>{{ item.getTax().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValueAfterTax().toFormat('0.00') }}</td>
                            <td class="warning--text font-weight-bold">{{ item.getPriceDifference().toFormat('0.00') }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            key="print"
                                            :disabled="!item.recorded_at"
                                            @click="print(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon :disabled="!item.recorded_at">fal fa-print</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.print') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-list-item
                                            key="view"
                                            @click="openForm(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-eye</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.view') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            key="delete"
                                            :disabled="!!item.recorded_at"
                                            @click="deleteItem(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon
                                                    :disabled="!!item.recorded_at"
                                                    :class="{'error--text': !item.recorded_at}"
                                                >
                                                    fal fa-times
                                                </v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title :class="{'error--text': !item.recorded_at}">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import PriceLeveling from '@/models/PriceLeveling';
import PriceLevelingsSearch from '@/views/shared/admin/priceLevelings/PriceLevelingsSearch';
import HtmlGen from '@/wrappers/HtmlGen';

export default {
    components: {
        PriceLevelingsSearch,
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,

            filter: {
                search: null,
                startDate: moment().subtract(1, 'month').format('YYYY-MM-DD') + ' 00:00:00',
                endDate: moment().format('YYYY-MM-DD') + ' 23:59:59',
            },

            headers: [
                { text: this.$t('general.date'), value: 'date' },
                { text: this.$t('general.recorded'), value: 'recorded_at' },
                { text: this.$t('general.number'), value: 'number' },
                { text: this.$t('price_levelings.description'), value: 'description', sortable: false },
                { text: this.$t('price_levelings.old_selling_value'), value: 'old_selling_value', sortable: false },
                { text: this.$t('price_levelings.old_tax'), value: 'old_tax', sortable: false },
                { text: this.$t('price_levelings.old_selling_value_after_tax'), value: 'old_selling_value_after_tax', sortable: false },
                { text: this.$t('price_levelings.selling_value'), value: 'selling_value', sortable: false },
                { text: this.$t('price_levelings.tax'), value: 'tax', sortable: false },
                { text: this.$t('price_levelings.selling_value_after_tax'), value: 'selling_value_after_tax', sortable: false },
                { text: this.$t('price_levelings.price_difference'), value: 'price_difference', sortable: false },
                { text: '', sortable: false }
            ],

            pagination: {
                sortBy: ['date'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        filter() {
            this.getItems();
        },
    },

    methods: {
        getItems() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/price-levelings?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;

            if (this.filter) {
                Object.keys(this.filter).forEach(key => {
                    if(this.filter[key]) {
                        url += `&${key}=${this.filter[key]}`;
                    }
                })
            }

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.items = response.data.data.map(item => new PriceLeveling(item));
                this.itemsTotal = response.data.total;
            });
        },

        openForm(item = null) {
            if(!item) {
                this.$router.push({ name: 'adminPriceLevelingCreate' }).catch(err => {});
                return;
            }

            this.$router.push({ name: 'adminPriceLevelingUpdate', params: { id: item.id } }).catch(err => {});
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('price_levelings.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    ApiService.delete(`/admin/price-levelings/${item.id}`, {}).then(response => {
                        this.items = this.items.filter(i => i.id !== item.id);
                        this.$snackbar(this.$t('price_levelings.deleted'), 'success');
                        this.itemsTotal--;
                    });
                }
            );
        },

        print(item) {
            const html = item.toTextLines().map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },

        async exportToFile(){
            const response = await ApiService.get(`/admin/price-levelings/export`, {
                responseType: 'blob',
                params: { start_date: this.filter.startDate, end_date: this.filter.endDate },
                timeout: 120000
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', this.$t('price_levelings.export_file', {
                value1: moment(this.filter.startDate).format('YYYYMMDDTHHmmss'),
                value2: moment(this.filter.endDate).format('YYYYMMDDTHHmmss'),
            }) + '.xlsx');
            document.body.appendChild(link);
            link.click();
        }
    }
};
</script>

<style lang="scss" scoped>
.price-levelings {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }
}
</style>
